import { useEffect, useMemo } from "react";
import { Box, Grid2, SxProps, Theme } from "@mui/material";
import { RouteComponentProps, useLocation } from "react-router";
import { Redirect, Route, Switch } from "react-router-dom";
import { RootState } from "./rootReducer";
import { useSelector } from "./helpers";
import "./App.css";
import "./icons.bundle";
import ResponsiveDrawer from "../components/Menu/ResponsiveDrawer/responsive-drawer.component";
import { HumanResourcesReport } from "../components/Reports/HumanResourcesReport/HumanResourcesReport";
import SupervisorReport from "../components/Reports/SupervisorReport/supervisor-report.component";
import AuthorizedRoute from "../components/Routing/authorized-route.component";
import Admin from "../features/Admin/admin.page";
import BookingForm from "../features/Booking-Form/booking-form.component";
import Capacity from "../features/Capacity/capacity-page.component";
import { Checkin } from "../features/Checkin/checkin.component";
import Colleagues from "../features/Connections/Colleagues/colleagues.component";
import Dashboard from "../features/Dashboard/dashboard.component";
import Dashboard2 from "../features/Dashboard2/dashboard2.component";
import Finance from "../features/Finance/finance.component";
import FacilityRouter from "../features/FloorManager/facility.router";
import { UserRole } from "../features/Login/typings/auth.types";
import { PricingModel } from "../features/Login/typings/login.types";
import BookingActivities from "../features/Reports/booking-activities.page";
import Occupation from "../features/Reports/occupation-report.page";
import Reports from "../features/Reports/reports.page";
import Schedule from "../features/Schedule/schedule.component";
import { NotificationsPage } from "../features/notifications/notifications.page";
import InventoryPage from "../components/InventoryManagement/Inventory.page";
import { NotificationSettingsPage } from "../components/Notification/NotificationSettings.page";
import ManagementPage from "../features/Management/management.page";
import ManagementLocationDetailPage from "../features/Management/management-location-detail.page";
import ManagementUserStatusDetailPage from "../features/Management/management-user-status-detail.page";

/**
 * Array of paths that shouldn't have content padding
 */
const noPaddingRoutes: string[] = ["/facility/schedule", "/capacity/map"];

/**
 * @description Component that manages the routing.
 * @version 0.2.0
 */
const App: React.FC<RouteComponentProps> = props => {
  const context = useSelector((state: RootState) => state.login);
  const { meta: metaData } = useSelector((state: RootState) => state.login.userInformation.company);

  const { pathname } = useLocation();
  const { path } = props.match;

  const contentPaddingSx = useMemo<SxProps<Theme>>(() => {
    if (noPaddingRoutes.includes(pathname)) {
      return { padding: "0", maxWidth: "100%" };
    }

    if (pathname === "/dashboard2") {
      return {
        padding: "3%",
        width: "100%",
        boxSizing: "border-box",
        height: { xs: "calc(100dvh - 56px)", sm: "calc(100dvh - 64px)" },
        maxHeight: { xs: "calc(100dvh - 56px)", sm: "calc(100dvh - 64px)" },
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "auto",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch"
      };
    }

    return {
      padding: "3%",
      background: "",
      height: "100%",
      width: "100%"
    };
  }, [pathname]);

  const isDashboard2 = pathname === "/dashboard2";

  const routeOutletSx = useMemo<SxProps<Theme>>(
    () =>
      isDashboard2
        ? {
            display: "flex",
            flexDirection: "column",
            flex: "0 0 auto",
            width: "100%",
            overflow: "visible"
          }
        : { width: "100%" },
    [isDashboard2]
  );

  useEffect(() => {
    if (!metaData) {
      props.history.push("/onboarding");
    } /*else if (context.requireSettings) {
      props.history.push("/my-settings");
    }*/
  }, [context.requireSettings, context.userRoles.isTenantAdmin, metaData, props.history]);

  return (
    <ResponsiveDrawer>
      <Grid2 sx={contentPaddingSx} data-testid="app-parent">
        <Box sx={routeOutletSx}>
          <Switch>
            <Route exact path={"/"} component={Dashboard} />
            <Route exact path={"/dashboard2"} component={Dashboard2} />
            <Route exact path={"/checkin"} component={Checkin} />
            <Route exact path={"/booking/:step"}>
              <BookingForm />
            </Route>
            <Route exact path={"/schedule"} component={Schedule} />
            <AuthorizedRoute
              roles={[
                UserRole.isProjectResponsible,
                UserRole.isCostCenterResponsible,
                UserRole.isCapacityResponsible,
                UserRole.isEmployeeResponsible,
                UserRole.isHrAdmin
              ]}
              exact
              path={"/report/:dest"}
              pricing={[PricingModel.ENTERPRISE]}
              component={Reports}
            />
            <AuthorizedRoute
              roles={[
                UserRole.isCapacityResponsible,
                UserRole.isCompanyAdmin,
                UserRole.isFleetAdmin
              ]}
              exact
              path={"/capacity/:dest"}
              component={Capacity}
            />
            <AuthorizedRoute
              roles={[UserRole.isFinanceAdmin]}
              exact
              path={"/finance"}
              pricing={[PricingModel.ENTERPRISE]}
              component={Finance}
            />
            <AuthorizedRoute
              roles={[UserRole.isEmployeeResponsible, UserRole.isHrAdmin]}
              exact
              path={"/supervisor"}
              component={SupervisorReport}
            />
            <AuthorizedRoute
              roles={[UserRole.isEmployeeResponsible, UserRole.isHrAdmin]}
              exact
              path={"/hr-report"}
              component={HumanResourcesReport}
            />
            <AuthorizedRoute
              roles={[UserRole.isTenantAdmin, UserRole.isCompanyAdmin]}
              exact
              path={"/management"}
              component={ManagementPage}
            />
            <AuthorizedRoute
              roles={[UserRole.isTenantAdmin, UserRole.isCompanyAdmin]}
              exact
              path={"/management/location"}
              component={ManagementLocationDetailPage}
            />
            <AuthorizedRoute
              roles={[UserRole.isTenantAdmin, UserRole.isCompanyAdmin]}
              exact
              path={"/management/users"}
              component={ManagementUserStatusDetailPage}
            />
            <Route exact path={"/connections"} component={Colleagues} />
            <AuthorizedRoute
              roles={[UserRole.isHrAdmin]}
              exact
              path={"/occupation"}
              component={Occupation}
            />
            <AuthorizedRoute
              roles={[UserRole.isEquipmentResponsible, UserRole.isFleetAdmin]}
              exact
              path={"/equipment/:dest"}
              component={InventoryPage}
            />
            <AuthorizedRoute
              roles={[UserRole.isEmployeeResponsible, UserRole.isHrAdmin]}
              exact
              path={"/booking-activities/:dest"}
              component={BookingActivities}
            />
            <Route exact path={"/notifications"} component={NotificationsPage} />
            <Route exact path={"/notification-settings"} component={NotificationSettingsPage} />
            <AuthorizedRoute
              roles={[
                UserRole.isCompanyAdmin,
                UserRole.isLocationAdmin,
                UserRole.isFinanceAdmin,
                UserRole.isHrAdmin,
                UserRole.isTenantAdmin,
                UserRole.isRequestAdmin,
                UserRole.isFleetAdmin,
                UserRole.isVisitorAdmin
              ]}
              exact
              path={"/admin/:dest/:subDest?"}
              component={Admin}
            />
            <AuthorizedRoute
              roles={[
                UserRole.isCompanyAdmin,
                UserRole.isLocationAdmin,
                UserRole.isFinanceAdmin,
                UserRole.isHrAdmin,
                UserRole.isTenantAdmin
              ]}
              exact
              path={"/facility/:dest"}
              component={FacilityRouter}
            />

            <Route path={path}>
              <Redirect to={"/"} />
            </Route>
          </Switch>
        </Box>
      </Grid2>
    </ResponsiveDrawer>
  );
};

export default App;
